import type { ICountryService } from "app/services/CountryService";

type StringKeys<T> = Extract<
    {
        [K in keyof T]: T[K] extends string ? K : never;
    }[keyof T],
    keyof T
>;

type NumberKeys<T> = Extract<
    {
        [K in keyof T]: T[K] extends number ? K : never;
    }[keyof T],
    keyof T
>;

type BooleanKeys<T> = Extract<
    {
        [K in keyof T]: T[K] extends boolean ? K : never;
    }[keyof T],
    keyof T
>;

export const createColumnsService = (countryService: ICountryService) => ({
    stringSorter:
        <T>(key: StringKeys<T>) =>
        (a: T, b: T) =>
            String(a[key]).localeCompare(String(b[key])),

    numberSorter:
        <T>(key: NumberKeys<T>) =>
        (a: T, b: T) => {
            const aValue = a[key];
            const bValue = b[key];

            const aMissing = aValue === null || aValue === undefined;
            const bMissing = bValue === null || bValue === undefined;

            if (aMissing && bMissing) {
                return 0;
            }
            if (aMissing) {
                return 1; // a is missing, sort it after b
            }
            if (bMissing) {
                return -1; // b is missing, sort it after a
            }

            return Number(aValue) - Number(bValue);
        },

    booleanSorter:
        <T>(key: BooleanKeys<T>) =>
        (a: T, b: T) =>
            (a[key] ? 1 : 0) - (b[key] ? 1 : 0),

    countrySorter:
        <T>(key: NumberKeys<T>) =>
        (a: T, b: T) => {
            const countryA = countryService.getCountryById(Number(a[key]))?.text ?? "";
            const countryB = countryService.getCountryById(Number(b[key]))?.text ?? "";

            return countryA.localeCompare(countryB);
        },
});
